import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { trackEvent } from "@/lib/analytics";

const reasons = ["School", "Creative opportunity", "Feedback", "Other"];

type FormStatus = "idle" | "submitting" | "sent" | "error";

export default function ContactPage() {
  const navigate = useNavigate();
  const loadedAt = useMemo(() => Date.now(), []);
  const [status, setStatus] = useState<FormStatus>("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const [form, setForm] = useState({
    name: "",
    email: "",
    reason: reasons[0],
    message: "",
    company: "",
  });

  const updateField = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    setForm((current) => ({
      ...current,
      [e.target.name]: e.target.value,
    }));
  };

  const submitForm = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus("submitting");
    setErrorMessage("");
    void trackEvent("contact_form_submit", {
      reason: form.reason,
    });

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, loadedAt }),
      });
      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(result.message || "The message could not be sent.");
      }

      setStatus("sent");
      void trackEvent("contact_form_success", {
        reason: form.reason,
      });
      setForm({
        name: "",
        email: "",
        reason: reasons[0],
        message: "",
        company: "",
      });
    } catch (error) {
      setStatus("error");
      void trackEvent("contact_form_error", {
        reason: form.reason,
      });
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "The message could not be sent.",
      );
    }
  };

  return (
    <div className="min-h-screen bg-stem-cream">
      <Navbar />
      <main>
        <section className="pt-28 md:pt-36 pb-10 md:pb-16 bg-stem-dark">
          <div className="section-padding max-w-4xl mx-auto">
            <button
              onClick={() => navigate("/")}
              className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm font-medium mb-4 md:mb-6 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line" />
              Back to Home
            </button>
            <span className="label text-revelBlue mb-3 block">Contact</span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-3">
              Get in Touch
            </h1>
            <p className="text-white/60 text-sm md:text-base max-w-xl">
              Messages are reviewed by a parent or guardian before reaching
              Danial.
            </p>
          </div>
        </section>

        <section className="py-16 md:py-24">
          <div className="section-padding max-w-3xl mx-auto">
            <form
              onSubmit={submitForm}
              className="bg-white rounded-2xl p-5 md:p-8 space-y-5"
            >
              <input
                type="text"
                name="company"
                value={form.company}
                onChange={updateField}
                tabIndex={-1}
                autoComplete="off"
                className="hidden"
                aria-hidden="true"
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <label className="block">
                  <span className="label text-stem-gray block mb-2">Name</span>
                  <input
                    name="name"
                    value={form.name}
                    onChange={updateField}
                    required
                    maxLength={80}
                    className="w-full rounded-xl border border-stem-sand px-4 py-3 text-stem-dark outline-none focus:border-revelBlue"
                  />
                </label>
                <label className="block">
                  <span className="label text-stem-gray block mb-2">Email</span>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={updateField}
                    required
                    maxLength={120}
                    className="w-full rounded-xl border border-stem-sand px-4 py-3 text-stem-dark outline-none focus:border-revelBlue"
                  />
                </label>
              </div>

              <label className="block">
                <span className="label text-stem-gray block mb-2">Reason</span>
                <select
                  name="reason"
                  value={form.reason}
                  onChange={updateField}
                  className="w-full rounded-xl border border-stem-sand px-4 py-3 text-stem-dark outline-none focus:border-revelBlue"
                >
                  {reasons.map((reason) => (
                    <option key={reason} value={reason}>
                      {reason}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block">
                <span className="label text-stem-gray block mb-2">Message</span>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={updateField}
                  required
                  maxLength={2000}
                  rows={7}
                  className="w-full rounded-xl border border-stem-sand px-4 py-3 text-stem-dark outline-none focus:border-revelBlue resize-y"
                />
              </label>

              {status === "sent" && (
                <p className="rounded-xl bg-revelBlue/10 px-4 py-3 text-sm text-revelBlue">
                  Message sent. Thank you.
                </p>
              )}
              {status === "error" && errorMessage && (
                <p className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">
                  {errorMessage}
                </p>
              )}

              <button
                type="submit"
                disabled={status === "submitting"}
                className="btn-dark text-sm disabled:opacity-50 disabled:hover:scale-100"
              >
                {status === "submitting" ? "Sending..." : "Send Message"}
                <i className="ri-send-plane-line" />
              </button>
            </form>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
